/**
 * src/lib/services/auth.ts
 * Authentication service. Phase 4: real API calls.
 */

import type { User } from '@/lib/types/auth';
import { get, patch, post } from '@/lib/api/client';

type MeResponse = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  cell_number: string | null;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  cell_number_verified: boolean;
  email_verified: boolean;
  verification_method: 'sms' | 'email';
};

// Raw fetch for login so a 401 shows an error instead of triggering the
// client's refresh-then-redirect cycle, which would swallow the message.
// These two calls bypass client.ts's request() wrapper entirely, so they
// need their own X-Requested-With: XMLHttpRequest header — see client.ts's
// module docstring (W17 security audit, Finding #7) for why the gateway
// requires it on every mutating request.
export async function login(email: string, password: string): Promise<void> {
  const base = (import.meta.env.VITE_API_BASE_URL as string) ?? '';
  const res = await fetch(`${base}/api/v1/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ email, password }),
  });
  if (res.status === 401) throw new Error('Invalid email or password');
  if (!res.ok) throw new Error('Login failed');
}

export async function logout(): Promise<void> {
  await post<void>('/api/v1/auth/logout');
}

export async function refreshSession(): Promise<void> {
  const base = (import.meta.env.VITE_API_BASE_URL as string) ?? '';
  const res = await fetch(`${base}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!res.ok) throw new Error('Session refresh failed');
}

export async function getMe(): Promise<User> {
  const data = await get<MeResponse>('/api/v1/auth/me');
  return { ...data, date_joined: data.created_at };
}

export async function updateProfile(updates: {
  first_name?: string;
  last_name?: string;
  cell_number?: string;
}): Promise<User> {
  const data = await patch<MeResponse>('/api/v1/auth/me', updates);
  return { ...data, date_joined: data.created_at };
}

export async function verifyOtp(otp: string): Promise<void> {
  await post<void>('/api/v1/auth/otp/verify', { otp });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await post<void>('/api/v1/auth/password/reset', { email });
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  await post<void>('/api/v1/auth/password/reset/confirm', {
    token,
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
}

/**
 * In-session password change (Settings page) for an operator who knows
 * their current password. Distinct from requestPasswordReset() /
 * confirmPasswordReset() above, which are the forgot-password flow for an
 * operator who is signed out and doesn't know their password.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  await post<void>('/api/v1/auth/password/change', {
    current_password: currentPassword,
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
}

export async function requestEmailChange(
  newEmail: string,
  password: string
): Promise<void> {
  await post<void>('/api/v1/auth/email/change', {
    new_email: newEmail,
    password,
  });
}

export async function verifyEmailChange(token: string): Promise<void> {
  await get<void>(`/api/v1/auth/email/verify?token=${token}`);
}

/**
 * Support ticket (Help & Support page). Emails Timothy Musama directly —
 * there is no shared support inbox for this platform.
 */
export async function submitSupportTicket(issueType: string, message: string): Promise<void> {
  await post<void>('/api/v1/auth/support/ticket', {
    issue_type: issueType,
    message,
  });
}
